import type {
  DashboardFilters,
  ExportRequest,
  ExportResult,
} from '@delivery-metrics/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getDashboardConfig } from '../config/env';
import {
  DashboardApiService,
  OrganizationApiService,
} from '../services/dashboard-api';
import { PythonApiService } from '../services/python-api';

// Query keys for React Query
export const QUERY_KEYS = {
  LEADERSHIP_METRICS: 'leadership-metrics',
  SCRUM_MASTER_METRICS: 'scrum-master-metrics',
  DEVELOPER_METRICS: 'developer-metrics',
  TEAMS: 'teams',
  UNITS: 'units',
  PROGRAMS: 'programs',
  UNIT_PROGRAM_MAP: 'unit-program-map',
  REPOSITORIES: 'repositories',
  DATA_SOURCES: 'data-sources',
  EXPORT_STATUS: 'export-status',
} as const;

/**
 * Hook for Leadership Dashboard metrics
 */
export function useLeadershipMetrics(filters: DashboardFilters) {
  const { refreshInterval } = getDashboardConfig();

  // The leadership GET endpoint is scoped only by program/unit/date. Key the
  // query on just those fields so toggling data-source drill-downs
  // (repositories/areaPaths/sonarqubeProjects) does not trigger a redundant
  // refetch — those are applied via the per-card POST calls in
  // EIRnDTopLevelKPISection, not this endpoint.
  const scopedKey = {
    programs: filters.programs,
    units: filters.units,
    timeRange: filters.timeRange,
  };

  return useQuery({
    queryKey: [QUERY_KEYS.LEADERSHIP_METRICS, scopedKey],
    queryFn: () => DashboardApiService.getLeadershipMetrics(filters),
    staleTime: refreshInterval / 2, // Consider data stale after half the refresh interval
    refetchInterval: refreshInterval,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors (client errors)
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook for Scrum Master Dashboard metrics
 */
export function useScrumMasterMetrics(filters: DashboardFilters) {
  const { refreshInterval } = getDashboardConfig();

  return useQuery({
    queryKey: [QUERY_KEYS.SCRUM_MASTER_METRICS, filters],
    queryFn: () => DashboardApiService.getScrumMasterMetrics(filters),
    staleTime: refreshInterval / 2,
    refetchInterval: refreshInterval,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook for Developer Dashboard metrics
 */
export function useDeveloperMetrics(filters: DashboardFilters) {
  const { refreshInterval } = getDashboardConfig();

  return useQuery({
    queryKey: [QUERY_KEYS.DEVELOPER_METRICS, filters],
    queryFn: () => DashboardApiService.getDeveloperMetrics(filters),
    staleTime: refreshInterval / 2,
    refetchInterval: refreshInterval,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook for available teams
 */
export function useTeams() {
  return useQuery({
    queryKey: [QUERY_KEYS.TEAMS],
    queryFn: () => OrganizationApiService.getTeams(),
    staleTime: 10 * 60 * 1000, // Teams don't change often, cache for 10 minutes
    retry: 2,
  });
}

/**
 * Hook for available programs
 */
export function usePrograms() {
  return useQuery({
    queryKey: [QUERY_KEYS.PROGRAMS],
    queryFn: () => OrganizationApiService.getPrograms(),
    staleTime: 10 * 60 * 1000, // Programs don't change often, cache for 10 minutes
    retry: 2,
  });
}

/**
 * Hook for available business units
 */
export function useUnits() {
  return useQuery({
    queryKey: [QUERY_KEYS.UNITS],
    queryFn: () => OrganizationApiService.getUnits(),
    staleTime: 10 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook for unit -> programs mapping
 */
export function useUnitProgramMap() {
  return useQuery({
    queryKey: [QUERY_KEYS.UNIT_PROGRAM_MAP],
    queryFn: () => OrganizationApiService.getUnitProgramMap(),
    staleTime: 10 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook for available repositories
 */
export function useRepositories() {
  return useQuery({
    queryKey: [QUERY_KEYS.REPOSITORIES],
    queryFn: () => OrganizationApiService.getRepositories(),
    staleTime: 10 * 60 * 1000, // Repositories don't change often, cache for 10 minutes
    retry: 2,
  });
}

/**
 * Hook for the data-source options available within the selected program/unit
 * scope (used to populate the leadership "Data Sources" filter). Re-fetches
 * when the selected programs or units change. Disabled until a Product Team
 * (program) is selected — the filters are hidden until then, and querying with
 * an empty scope would return a full distinct set across the entire dataset.
 */
export function useDataSources(filters: DashboardFilters) {
  return useQuery({
    queryKey: [
      QUERY_KEYS.DATA_SOURCES,
      filters.programs ?? [],
      filters.units ?? [],
    ],
    queryFn: () => PythonApiService.getAvailableDataSources(filters),
    enabled: (filters.programs?.length ?? 0) > 0,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook for exporting dashboard data
 */
export function useExportDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (exportRequest: ExportRequest) =>
      DashboardApiService.exportDashboard(exportRequest),
    onSuccess: (data: ExportResult) => {
      // Cache the export result for status polling
      queryClient.setQueryData([QUERY_KEYS.EXPORT_STATUS, data.id], data);
    },
  });
}

/**
 * Hook for polling export status
 */
export function useExportStatus(
  exportId: string | null,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: [QUERY_KEYS.EXPORT_STATUS, exportId],
    queryFn: () => DashboardApiService.getExportStatus(exportId!),
    enabled: enabled && !!exportId,
    refetchInterval: query => {
      // Stop polling when export is completed or failed
      const exportData = query.state.data;
      if (
        exportData?.status === 'completed' ||
        exportData?.status === 'failed'
      ) {
        return false;
      }
      return 2000; // Poll every 2 seconds
    },
    retry: false, // Don't retry failed status checks
  });
}

/**
 * Hook for downloading export files
 */
export function useDownloadExport() {
  return useMutation({
    mutationFn: ({
      exportId,
      filename,
    }: {
      exportId: string;
      filename?: string;
    }) => DashboardApiService.downloadExport(exportId, filename),
  });
}

/**
 * Hook for invalidating dashboard data (useful for manual refresh)
 */
export function useInvalidateDashboardData() {
  const queryClient = useQueryClient();

  return {
    invalidateLeadershipMetrics: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.LEADERSHIP_METRICS],
      }),
    invalidateScrumMasterMetrics: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SCRUM_MASTER_METRICS],
      }),
    invalidateDeveloperMetrics: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DEVELOPER_METRICS],
      }),
    invalidateAllMetrics: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.LEADERSHIP_METRICS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SCRUM_MASTER_METRICS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DEVELOPER_METRICS],
      });
    },
    invalidateOrganizationData: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAMS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UNITS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROGRAMS] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.UNIT_PROGRAM_MAP],
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REPOSITORIES] });
    },
  };
}
